import axios from "axios";
import type { Dispatch } from "react";
import { useTranslation } from "react-i18next";
import type { TemplateExportDocument } from "../../../../../../components/DownloadTemplateFiles/DownloadTemplateFiles";
import { TemplateFilesDownloadPanel } from "../../../../../../components/DownloadTemplateFiles/DownloadTemplateFiles";
import { endpoints } from "../../../../../../endpoints";
import type { OptionType } from "../../../../../../types/types";
import type {
  EntityExportStatusSchema,
  EntityType,
} from "../../../../../../types/types.MDM";
import { useMdmEntityDisplayNames } from "../../../../../../util/mdmEntityDisplayNames";
import { useStoreState } from "../../../../../../util/util";
import type {
  EntityUploadAction,
  EntityUploadState,
} from "./createEntityFromUploads.util";
import { DEFAULT_STATUSES } from "./createEntityFromUploads.util";
import { CreateEntitiesList } from "./CreateEntitiesList";

export const DownloadEntityTemplateFiles = ({
  template,
  entityType,
  dispatch,
  export_id,
  selected_entities,
}: {
  template: OptionType<string>;
  entityType: EntityType;
  dispatch: Dispatch<EntityUploadAction>;
  export_id?: string;
  selected_entities?: EntityUploadState["selected_entities"];
}) => {
  const { t } = useTranslation();
  const { storefront_id, tenant_id } = useStoreState();
  const { getLabel } = useMdmEntityDisplayNames();
  const entityLabelPlural = getLabel(entityType, "plural").toLowerCase();
  // @see server/src/agilis/routes/v2.py - export routes are registered per
  // plural entity type (suppliers/distributors/customers), not a generic one.
  const entityTypePlural = `${entityType}s`;

  return (
    <TemplateFilesDownloadPanel
      template={template}
      tenantId={tenant_id}
      exportId={export_id}
      defaultSelectedItems={selected_entities}
      copy={{
        description: t(
          "The following CSV files contain the attributes and attribute types for the selected template. Download these files to update existing entity data or to create new entities."
        ),
        reDownloadNote: t(
          "Please do not modify the first rows containing attribute details. We also recommend keeping the same file name."
        ),
        includeExistingDataLabel: t("Include existing {{label}} data", {
          label: entityLabelPlural,
        }),
        groupsSectionNote: t("This file is required to create entity profiles"),
        collectionsSectionNote: t(
          "These files are used to update existing entities with collection data"
        ),
        preparingFilesMessage: t(
          "Please wait, your {{label}} data is being prepared for download...",
          { label: entityLabelPlural }
        ),
        pollFailedMessage: t(
          "Could not fetch existing {{label}} data. Please try again later.",
          { label: entityLabelPlural }
        ),
        genericExportError: t("Could not fetch existing {{label}} data.", {
          label: entityLabelPlural,
        }),
      }}
      onSetExportId={(next_export_id) =>
        dispatch({
          type: "set_export_id",
          payload: { export_id: next_export_id } as EntityUploadState,
        })
      }
      onSetSelectedItems={(selected_entities) =>
        dispatch({
          type: "set_selected_entities",
          payload: { selected_entities } as EntityUploadState,
        })
      }
      onMoveBack={() =>
        dispatch({
          type: "move_back",
          payload: { uploadStage: "download" } as EntityUploadState,
        })
      }
      onContinueFromDownload={(collectionFileNames) =>
        dispatch({
          type: "continue_from_download",
          payload: { collectionFileNames } as EntityUploadState,
        })
      }
      startExport={async (selected_items) => {
        const params = new URLSearchParams({ template: template.label });
        DEFAULT_STATUSES.forEach((status) => params.append("status", status));
        params.append("limit", "100000000");
        if (selected_items !== "all" && selected_items.length) {
          selected_items.forEach((entity_id) =>
            params.append("entity_profile_id", entity_id)
          );
        }
        const { data } = await axios.post<EntityExportStatusSchema>(
          endpoints.v2_storefronts_id_entity_type_export_async(
            storefront_id,
            entityTypePlural
          ),
          {},
          { params }
        );
        return { id: data.id };
      }}
      pollExportStatus={async (
        job_id
      ): Promise<{
        status: EntityExportStatusSchema["status"];
        documents: TemplateExportDocument[];
      }> => {
        const {
          data: { status, document },
        } = await axios.get<EntityExportStatusSchema>(
          endpoints.v2_storefronts_id_entity_type_export_job_id(
            storefront_id,
            entityTypePlural,
            job_id
          )
        );
        return {
          status,
          documents: document
            ? [
                {
                  id: document.id,
                  name: document.name,
                  url: document.signed_url,
                },
              ]
            : [],
        };
      }}
      renderSpecificItemsSelector={({
        handleSelectRows,
        selectedTemplate,
        defaultSelectedRows,
      }) => (
        <CreateEntitiesList
          entityType={entityType}
          handleSelectRows={handleSelectRows}
          selectedTemplate={selectedTemplate}
          defaultSelectedRows={defaultSelectedRows}
        />
      )}
    />
  );
};
